/**
 * Typed driver.js wrapper (tour spec §6).
 *
 * Owns everything driver-specific: visible-anchor resolution, position
 * (desktop vs mobile), progress text, reduced motion, and the a11y patches
 * for driver.js's known axe gaps (aria-labels on Next/Back/Close via
 * onPopoverRender; the aria-live region itself lives in TourProvider and is
 * fed through the onStep callback).
 */

import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tours.css';
import type { TourDef, TourStepDef } from './registry';
import { STRINGS, type TourLang } from './strings';

export interface ResolvedStep {
  def: TourStepDef;
  element: Element;
}

/** First visible match wins (desktop sidebar anchor vs mobile bottom-nav twin). */
export function resolveVisibleTarget(targets: string[]): Element | null {
  for (const sel of targets) {
    const els = document.querySelectorAll(sel);
    for (const el of Array.from(els)) {
      if (el.getClientRects().length > 0) return el;
    }
  }
  return null;
}

/** Steps whose anchor is present AND visible right now, in registry order. */
export function resolveSteps(tour: TourDef): ResolvedStep[] {
  const out: ResolvedStep[] = [];
  for (const def of tour.steps) {
    const element = resolveVisibleTarget(def.targets);
    if (element) out.push({ def, element });
    else if (import.meta.env.DEV) {
      // Dev-mode assertion from spec §3: a registered selector is missing.
      console.warn(`[tours] anchor missing for ${tour.id} step "${def.key}":`, def.targets);
    }
  }
  return out;
}

export function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

export function isMobileViewport(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(max-width: 1023px)').matches // below Tailwind lg
    : false;
}

export interface CreateTourOpts {
  tour: TourDef;
  lang: TourLang;
  steps: ResolvedStep[];
  /** Fired on each highlighted step — feeds the aria-live region + analytics. */
  onStep: (stepIndex: number, total: number, title: string) => void;
  /**
   * Fired on ANY exit attempt (✕ / Esc / overlay click / Done). The provider
   * decides: complete when isDone, else show the skip-confirm. The driver
   * stays active until the provider calls .destroy() (driver.js's documented
   * onDestroyStarted contract — public destroy skips the hook).
   */
  onExitAttempt: (ctx: { stepIndex: number; total: number; isDone: boolean }) => void;
}

export function createTourDriver(opts: CreateTourOpts): Driver {
  const { tour, lang, steps, onStep, onExitAttempt } = opts;
  const chrome = STRINGS[lang].chrome;
  const stepCopy = STRINGS[lang].steps;
  const mobile = isMobileViewport();
  const reduced = prefersReducedMotion();
  const total = steps.length;

  const driveSteps: DriveStep[] = steps.map(({ def, element }) => {
    const copy = stepCopy[def.key] ?? { title: def.key, body: '' };
    return {
      element,
      popover: {
        title: copy.title,
        description: copy.body,
        side: mobile && def.mobileSide ? def.mobileSide : def.side,
        align: def.align ?? 'center',
      },
    };
  });

  // Config callbacks need the driver instance before `driver()` returns —
  // captured via a ref to keep TS definite-assignment happy.
  const dRef: { current: Driver | null } = { current: null };

  const announce = (index: number | undefined) => {
    const i = index ?? 0;
    const copy = stepCopy[steps[i]?.def.key ?? ''];
    onStep(i, total, copy?.title ?? '');
  };

  const d = driver({
    steps: driveSteps,
    showProgress: total > 1,
    progressText: chrome.progress,
    nextBtnText: chrome.next,
    prevBtnText: chrome.prev,
    doneBtnText: chrome.done,
    animate: !reduced,
    smoothScroll: !reduced,
    allowClose: true,
    disableActiveInteraction: true, // tap Next, never the highlighted element (§7)
    overlayOpacity: 0.62,
    stagePadding: 6,
    stageRadius: 10,
    popoverClass: 'urava-tour-popover',
    // Belt-and-braces: if an anchor disappears mid-tour (e.g. data refresh),
    // skip that step instead of showing a centered orphan popover.
    skipMissingElement: true,
    onPopoverRender: (popover, { state }) => {
      // driver.js's known axe gaps: label the controls (spec §6).
      popover.nextButton.setAttribute('aria-label', chrome.next);
      popover.previousButton.setAttribute('aria-label', chrome.prev);
      popover.closeButton.setAttribute('aria-label', chrome.closeAria);
      // data-track so the delegated analytics click listener records taps.
      const idx = (state.activeIndex ?? 0) + 1;
      popover.nextButton.setAttribute('data-track', `tour-${tour.id}-next-${idx}`);
      popover.previousButton.setAttribute('data-track', `tour-${tour.id}-prev-${idx}`);
      popover.closeButton.setAttribute('data-track', `tour-${tour.id}-close-${idx}`);
    },
    onHighlightStarted: (_el, _step, { state }) => {
      announce(state.activeIndex);
    },
    onDestroyStarted: () => {
      const inst = dRef.current;
      if (!inst) return;
      onExitAttempt({
        stepIndex: inst.getActiveIndex() ?? 0,
        total,
        isDone: !inst.hasNextStep(),
      });
    },
  });

  dRef.current = d;
  return d;
}

/** rAF-poll for ONE step's targets — resolves the element, or null at the cap. */
export function waitForTargets(targets: string[], capMs: number): Promise<Element | null> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const el = resolveVisibleTarget(targets);
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - started >= capMs) {
        resolve(null);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

export interface CrossRouteTourController {
  driver: Driver;
  total: number;
  /**
   * Navigate-aware move. Walks from `index` in `dir`: navigates to the
   * candidate step's route when it differs from the current pathname, waits
   * for its anchor (~4s after a navigation, shorter otherwise), and highlights
   * it. A step whose anchor never appears is skipped SILENTLY in the same
   * direction — the tour never hangs. Walking off the end forward completes
   * the tour; walking off the start is a no-op.
   */
  goTo: (index: number, dir?: 1 | -1) => void;
  /** First step index after `from` in a different chapter, or null. */
  nextChapterIndex: (from: number) => number | null;
  /** Invalidate any in-flight anchor wait (call on teardown). */
  cancel: () => void;
}

/**
 * Cross-page tour driver ("Show me around"). Differences from the same-route
 * driver above:
 *  - Steps are the FULL registry list; elements resolve lazily (functions),
 *    because most anchors live on routes that aren't mounted at create time.
 *  - Movement is fully owned by goTo() via the onNextClick/onPrevClick/
 *    onDoneClick overrides (keyboard arrows route through the same hooks).
 *  - Deliberately NO skipMissingElement: driver.js computes Next/Done labels
 *    and hasNextStep() from "next PRESENT element", and other-route anchors
 *    are always absent — with it on, every step would render as the last.
 *  - Per-step progressText carries the chapter ("Research · 4 of 13").
 */
export function createCrossRouteTour(opts: {
  tour: TourDef;
  lang: TourLang;
  navigate: (path: string) => void;
  onStep: (stepIndex: number, total: number, title: string) => void;
  onExitAttempt: (ctx: { stepIndex: number; total: number; isDone: boolean }) => void;
}): CrossRouteTourController {
  const { tour, lang, navigate, onStep, onExitAttempt } = opts;
  const chrome = STRINGS[lang].chrome;
  const stepCopy = STRINGS[lang].steps;
  const chapterNames = STRINGS[lang].chapters;
  const mobile = isMobileViewport();
  const reduced = prefersReducedMotion();
  const defs = tour.steps;
  const total = defs.length;

  const driveSteps: DriveStep[] = defs.map((def) => {
    const copy = stepCopy[def.key] ?? { title: def.key, body: '' };
    const chapterLabel = def.chapter ? chapterNames[def.chapter] ?? def.chapter : null;
    return {
      // Lazy: resolved at highlight time. goTo() verifies visibility just
      // before every move, so this is practically never null when called.
      element: () => resolveVisibleTarget(def.targets) as Element,
      popover: {
        title: copy.title,
        description: copy.body,
        side: mobile && def.mobileSide ? def.mobileSide : def.side,
        align: def.align ?? 'center',
        ...(chapterLabel ? { progressText: chrome.chapterProgress(chapterLabel) } : {}),
      },
    };
  });

  const dRef: { current: Driver | null } = { current: null };

  const announce = (index: number | undefined) => {
    const i = index ?? 0;
    const copy = stepCopy[defs[i]?.key ?? ''];
    onStep(i, total, copy?.title ?? '');
  };

  // A newer goTo (or cancel()) supersedes any in-flight anchor wait, and a
  // wait that resolves after the driver was destroyed must never restart it.
  let seq = 0;
  let started = false;

  const goTo = (index: number, dir: 1 | -1 = 1) => {
    const mySeq = ++seq;
    const attempt = (i: number) => {
      if (mySeq !== seq) return;
      if (i < 0) return; // walked off the start going Back — stay put
      if (i >= total) {
        onExitAttempt({ stepIndex: total - 1, total, isDone: true });
        return;
      }
      const def = defs[i];
      const needsNav = !!def.route && window.location.pathname !== def.route;
      if (needsNav) navigate(def.route!);
      // Generous cap after a navigation or at tour start (route chunks and
      // stores may still be loading); short otherwise so a genuinely absent
      // anchor (e.g. data-dependent) skips fast instead of stalling.
      // After a navigation (or at tour start) wait generously: intake pages are
      // route-lazy and the draft-type registry is an API call — on a cool
      // Render backend 4s silently dropped the whole Drafting chapter.
      waitForTargets(def.targets, needsNav || !started ? 12000 : 1200).then((el) => {
        if (mySeq !== seq) return;
        const d = dRef.current;
        if (!d) return;
        if (!el) {
          attempt(i + dir); // silent skip — the tour must never hang
          return;
        }
        if (d.isActive()) d.moveTo(i);
        else if (!started) {
          started = true;
          d.drive(i);
        }
        // started && !isActive → destroyed mid-wait: do nothing.
      });
    };
    attempt(index);
  };

  const d = driver({
    steps: driveSteps,
    showProgress: true,
    progressText: chrome.progress,
    nextBtnText: chrome.next,
    prevBtnText: chrome.prev,
    doneBtnText: chrome.done,
    animate: !reduced,
    smoothScroll: !reduced,
    allowClose: true,
    disableActiveInteraction: true,
    overlayOpacity: 0.62,
    stagePadding: 6,
    stageRadius: 10,
    popoverClass: 'urava-tour-popover',
    onPopoverRender: (popover, { state }) => {
      popover.nextButton.setAttribute('aria-label', chrome.next);
      popover.previousButton.setAttribute('aria-label', chrome.prev);
      popover.closeButton.setAttribute('aria-label', chrome.closeAria);
      const idx = (state.activeIndex ?? 0) + 1;
      popover.nextButton.setAttribute('data-track', `tour-${tour.id}-next-${idx}`);
      popover.previousButton.setAttribute('data-track', `tour-${tour.id}-prev-${idx}`);
      popover.closeButton.setAttribute('data-track', `tour-${tour.id}-close-${idx}`);
    },
    onHighlightStarted: (_el, _step, { state }) => {
      announce(state.activeIndex);
    },
    // Movement overrides: driver must never move by itself — every advance
    // may need a navigation + anchor wait first.
    onNextClick: () => goTo((dRef.current?.getActiveIndex() ?? 0) + 1, 1),
    onPrevClick: () => goTo((dRef.current?.getActiveIndex() ?? 0) - 1, -1),
    onDoneClick: () =>
      onExitAttempt({
        stepIndex: dRef.current?.getActiveIndex() ?? total - 1,
        total,
        isDone: true,
      }),
    onDestroyStarted: () => {
      const inst = dRef.current;
      if (!inst) return;
      // ✕ / Esc / overlay click — Done never reaches here (onDoneClick owns it).
      onExitAttempt({ stepIndex: inst.getActiveIndex() ?? 0, total, isDone: false });
    },
  });

  dRef.current = d;

  const nextChapterIndex = (from: number): number | null => {
    const cur = defs[from]?.chapter;
    for (let i = from + 1; i < total; i++) {
      if (defs[i].chapter && defs[i].chapter !== cur) return i;
    }
    return null;
  };

  return {
    driver: d,
    total,
    goTo,
    nextChapterIndex,
    cancel: () => {
      seq += 1;
    },
  };
}

/**
 * rAF-poll for a tour's first anchor (stores hydrate from cache, so anchors
 * appear fast — spec §6). Resolves true the moment any first-step target is
 * visible, false at the cap.
 */
export function waitForAnchors(tour: TourDef, capMs = 3000): Promise<boolean> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (tour.steps.some((s) => resolveVisibleTarget(s.targets))) {
        resolve(true);
        return;
      }
      if (Date.now() - started >= capMs) {
        resolve(false);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}
